import React, { useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';

interface ApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (comments: string) => void;
    isLoading?: boolean;
    patientName: string;
    primaryContactEmail: string;
    clientEmail?: string;
    error?: string | null;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    patientName,
    primaryContactEmail,
    clientEmail,
    error,
}) => {
    const [comments, setComments] = useState('');

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27 && !isLoading) onClose(); // ESC key
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose, isLoading]);

    const handleConfirm = () => {
        onConfirm(comments);
    };

    const handleClose = () => {
        setComments(''); // Reset comments when closing
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'
            onClick={() => {
                if (!isLoading) handleClose();
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='text-gray-600 hover:text-gray-900 absolute right-4 top-4'
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <div className='mb-6'>
                        <h2 className='mb-6 text-[22px] font-bold text-black'>
                            Approve Campaign Request
                        </h2>

                        <p className='text-gray-700 mb-4'>
                            You are about to approve the campaign request for{' '}
                            <span className='font-semibold'>{patientName}</span>
                            .
                        </p>

                        <p className='text-gray-700 mb-4'>
                            This will turn this request into an{' '}
                            <span className='inline-flex rounded-full bg-[#FDB462] px-2 py-1 text-sm font-medium text-black'>
                                In Progress
                            </span>{' '}
                            <span className='font-semibold'>
                                Campaign Application
                            </span>
                            .
                        </p>

                        <p className='text-gray-700 mb-6'>
                            This will also send an email to the following
                            {clientEmail && clientEmail !== primaryContactEmail ? ' Contacts' : ' Contact'}:
                        </p>

                        <div className='mb-6 space-y-2'>
                            {clientEmail && clientEmail === primaryContactEmail ? (
                                <div>
                                    <span className='font-medium'>
                                        Primary Contact / Client:{' '}
                                    </span>
                                    <span className='font-bold'>
                                        {primaryContactEmail}
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <span className='font-medium'>
                                            Primary Contact:{' '}
                                        </span>
                                        <span className='font-bold'>
                                            {primaryContactEmail}
                                        </span>
                                    </div>
                                    {clientEmail && (
                                        <div>
                                            <span className='font-medium'>
                                                Client:{' '}
                                            </span>
                                            <span className='font-bold'>
                                                {clientEmail}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className='mb-6'>
                            <label
                                htmlFor='comments'
                                className='mb-2 block font-medium text-black'
                            >
                                Notes (optional)
                            </label>
                            <textarea
                                id='comments'
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                disabled={isLoading}
                                className='border-gray-300 text-gray-700 placeholder-gray-400 disabled:bg-gray-100 w-full rounded-lg border p-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                                rows={6}
                            />
                        </div>

                        {error && (
                            <div className='mb-4 rounded-lg border-2 border-red-600 bg-red-100 p-4'>
                                <div className='flex items-start'>
                                    <div className='flex-shrink-0'>
                                        <svg
                                            className='h-6 w-6 text-red-600'
                                            xmlns='http://www.w3.org/2000/svg'
                                            viewBox='0 0 20 20'
                                            fill='currentColor'
                                            aria-hidden='true'
                                        >
                                            <path
                                                fillRule='evenodd'
                                                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z'
                                                clipRule='evenodd'
                                            />
                                        </svg>
                                    </div>
                                    <div className='ml-3 flex-1'>
                                        <h3 className='text-base font-bold text-red-900'>
                                            Approval Failed
                                        </h3>
                                        <div className='mt-2 text-sm font-medium text-red-800'>
                                            {error}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='flex justify-start space-x-4'>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className='border-gray-300 rounded-full border-[2px] bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isLoading || !!error}
                            className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isLoading ? 'APPROVING...' : 'APPROVE'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApprovalModal;
